import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, ShoppingCart, Minus, Plus, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../utils/api';
import { useCart } from '../contexts/CartContext';
import LoadingScreen from '../components/LoadingScreen';

const SIZE_LABELS = {
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
};

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null); // 'small' | 'medium' | 'large' | null

  const { addToCart } = useCart();

  useEffect(() => {
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProduct = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/products/${id}`);
      const p = response.data;
      setProduct(p);

      // Auto-select the first available size if the product uses sizes
      if (p.sizes) {
        const firstSize = ['small', 'medium', 'large'].find(s => p.sizes[s] != null && p.sizes[s] > 0);
        setSelectedSize(firstSize || null);
      } else {
        setSelectedSize(null);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      if (error.response?.status === 404) {
        navigate('/404');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Determine which sizes are available for this product
  const availableSizes = product && product.sizes
    ? ['small', 'medium', 'large'].filter(s => product.sizes[s] != null && product.sizes[s] > 0)
    : [];

  const hasSizes = availableSizes.length > 0;

  // Build dynamic carousel that always contains all size images + general images
  const carouselImages = React.useMemo(() => {
    const images = [];
    
    // Add size-specific images first, in order
    if (product?.sizeImages) {
      if (product.sizeImages.small?.url) {
        images.push({ url: product.sizeImages.small.url, sizeKey: 'small', _id: 'size-small' });
      }
      if (product.sizeImages.medium?.url) {
        images.push({ url: product.sizeImages.medium.url, sizeKey: 'medium', _id: 'size-medium' });
      }
      if (product.sizeImages.large?.url) {
        images.push({ url: product.sizeImages.large.url, sizeKey: 'large', _id: 'size-large' });
      }
    }

    // Add general images (avoiding duplicates if they accidentally uploaded the same picture)
    const existingUrls = new Set(images.map(img => img.url));
    if (product?.images) {
      for (const img of product.images) {
        if (!existingUrls.has(img.url)) {
          images.push({ ...img, sizeKey: null, _id: img._id || img.url });
          existingUrls.add(img.url);
        }
      }
    }

    if (images.length === 0) {
      images.push({ url: 'https://via.placeholder.com/600x600?text=No+Image', sizeKey: null, _id: 'placeholder' });
    }

    return images;
  }, [product]);

  // Ensure index safely within bounds
  const safeSelectedImage = selectedImage >= carouselImages.length ? 0 : selectedImage;
  const activeImageUrl = carouselImages[safeSelectedImage]?.url;
  
  const handleImageChange = (newIndex) => {
    setSelectedImage(newIndex);
    // If the image we just scrolled to is tied to a specific size, auto-select that size
    const imgObj = carouselImages[newIndex];
    if (imgObj?.sizeKey && availableSizes.includes(imgObj.sizeKey)) {
      setSelectedSize(imgObj.sizeKey);
    }
  };

  const handleSizeSelect = (size) => {
    setSelectedSize(size);
    // When size changes via button, jump to its specific picture in the carousel
    let index = carouselImages.findIndex(img => img.sizeKey === size);
    
    // Fallback: If no explicit size image was found, map the size's index to the available images
    if (index === -1 && carouselImages.length > 1) {
      const sizeIndex = availableSizes.indexOf(size);
      if (sizeIndex !== -1 && sizeIndex < carouselImages.length) {
        index = sizeIndex;
      }
    }

    if (index !== -1) {
      setSelectedImage(index);
    }
  };

  const displayedPrice = hasSizes && selectedSize
    ? product.sizes[selectedSize]
    : product?.price ?? 0;

  const handleAddToCart = () => {
    if (hasSizes && !selectedSize) return;
    const productWithPrice = hasSizes
      ? { ...product, price: product.sizes[selectedSize] }
      : product;
    addToCart(productWithPrice, quantity, hasSizes ? selectedSize : null);
  };

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= product.stockQuantity) {
      setQuantity(newQuantity);
    }
  };

  const carouselLength = carouselImages.length;

  const handlePrevImage = () => {
    handleImageChange(safeSelectedImage === 0 ? carouselLength - 1 : safeSelectedImage - 1);
  };

  const handleNextImage = () => {
    handleImageChange(safeSelectedImage === carouselLength - 1 ? 0 : safeSelectedImage + 1);
  };

  // Touch handlers for swipe
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEndHandler = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) handleNextImage();
    if (distance < -minSwipeDistance) handlePrevImage();
  };

  if (isLoading) {
    return <LoadingScreen message="Unveiling nature's beauty..." />;
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product not found</h2>
          <button onClick={() => navigate('/shop')} className="btn-primary">
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-32 lg:py-12 lg:pb-12">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-500 hover:text-emerald-600 mb-8 transition-all duration-300 font-medium group"
        >
          <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
          Back to collections
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          {/* Left Column: Product Images */}
          <div className="lg:col-span-5 xl:col-span-4 lg:pr-8">
            <div className="sticky top-24 max-w-[360px] lg:max-w-[420px] mx-auto">

              {/* Main Image with Arrow Buttons */}
              <div 
                className="relative aspect-[4/5] bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100 group"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEndHandler}
              >
                <img
                  key={activeImageUrl}
                  src={activeImageUrl}
                  alt={product.name}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
                  }}
                  draggable="false"
                  className="w-full h-full object-cover transition-opacity duration-300 select-none pointer-events-none animate-fade-in"
                />

                {/* Scroll Arrows — show when multiple general images OR if relying on general gallery */}
                {carouselLength > 1 && (
                  <>
                    <button
                      onClick={handlePrevImage}
                      aria-label="Previous image"
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 backdrop-blur-sm shadow-md hidden lg:flex items-center justify-center text-gray-700 hover:bg-white hover:text-emerald-600 transition-all duration-200 opacity-0 group-hover:opacity-100"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handleNextImage}
                      aria-label="Next image"
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 backdrop-blur-sm shadow-md hidden lg:flex items-center justify-center text-gray-700 hover:bg-white hover:text-emerald-600 transition-all duration-200 opacity-0 group-hover:opacity-100"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>

                    {/* Dot indicators */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                      {Array.from({ length: carouselLength }).map((_, i) => (
                        <button
                          key={i}
                          onClick={() => handleImageChange(i)}
                          aria-label={`Go to image ${i + 1}`}
                          className={`rounded-full transition-all duration-200 ${
                            safeSelectedImage === i
                              ? 'w-5 h-2 bg-emerald-500'
                              : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Thumbnail Row */}
              {carouselLength > 1 && (
                <div className="flex gap-3 mt-6 overflow-x-auto pb-2 scrollbar-hide">
                  {carouselImages.map((image, index) => (
                    <button
                      key={image._id || index}
                      onClick={() => handleImageChange(index)}
                      className={`flex-shrink-0 w-24 h-24 rounded-2xl overflow-hidden border-2 transition-all duration-300 ${safeSelectedImage === index
                        ? 'border-emerald-600 shadow-md ring-4 ring-emerald-50'
                        : 'border-white hover:border-emerald-200'
                        }`}
                    >
                      <img
                        src={image.url}
                        alt={`${product.name} ${index + 1}`}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/100x100?text=Error';
                          if (e.target.src.includes('via.placeholder')) {
                            e.target.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
                          }
                        }}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Product Info & Description */}
          <div className="lg:col-span-7 xl:col-span-8 flex flex-col pt-4">
            {/* Header Section */}
            <div className="mb-8 lg:mb-10">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="px-2 py-0.5 bg-emerald-100/50 text-emerald-800 text-[10px] font-bold rounded-md tracking-wider uppercase">
                  {product.category}
                </span>
                {product.featured && (
                  <span className="px-2 py-0.5 bg-amber-100/50 text-amber-800 text-[10px] font-bold rounded-md tracking-wider uppercase border border-amber-200/50">
                    Featured
                  </span>
                )}
              </div>

              <h1 className="text-3xl lg:text-5xl font-light text-gray-900 mb-4 lg:mb-6 leading-tight tracking-tight">
                {product.name}
              </h1>

              {/* Price */}
              <div className="flex items-baseline gap-4 lg:gap-6 mb-6 lg:mb-8">
                <span className="text-2xl lg:text-4xl font-normal text-emerald-600">
                  {(displayedPrice || 0).toLocaleString()}{' '}
                  <span className="text-base lg:text-xl font-light text-emerald-500/80">ETB</span>
                </span>
                {product.originalPrice && product.originalPrice > product.price && (
                  <span className="text-lg text-gray-400 line-through font-light">
                    {product.originalPrice.toLocaleString()} ETB
                  </span>
                )}
                <div className={`ml-auto px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest ${product.inStock ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {product.inStock ? 'In Stock' : 'Out of Stock'}
                </div>
              </div>

              {/* Ratings */}
              {product.ratings.count > 0 && (
                <div className="flex items-center gap-2 lg:gap-3 py-3 lg:py-4 border-y border-gray-100">
                  <div className="flex text-amber-400">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${i < Math.floor(product.ratings.average)
                          ? 'fill-current'
                          : 'text-gray-200'
                          }`}
                      />
                    ))}
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-gray-500">
                    {product.ratings.average.toFixed(1)} / 5.0
                  </span>
                  <span className="text-[10px] sm:text-xs text-gray-400 font-medium ml-1 lg:ml-2 uppercase tracking-wider">
                    ({product.ratings.count} reviews)
                  </span>
                </div>
              )}
            </div>

            {/* Size Selector */}
            {hasSizes && (
              <div className="mb-6 lg:mb-8">
                <div className="flex items-center gap-3 lg:gap-4 mb-4">
                  <h3 className="text-[10px] lg:text-sm font-black text-gray-300 uppercase tracking-[0.2em] whitespace-nowrap">
                    Select size
                  </h3>
                  <div className="h-[1px] lg:h-[2px] flex-1 bg-gradient-to-r from-gray-100 to-transparent" />
                </div>
                <div className="flex gap-3 flex-wrap">
                  {availableSizes.map(size => (
                    <button
                      key={size}
                      onClick={() => handleSizeSelect(size)}
                      className={`flex flex-col items-center px-5 py-3 rounded-2xl border-2 transition-all duration-200 min-w-[80px] ${
                        selectedSize === size
                          ? 'border-emerald-600 bg-emerald-50 shadow-sm ring-2 ring-emerald-100'
                          : 'border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/50'
                      }`}
                    >
                      <span className={`text-sm font-semibold uppercase tracking-wide ${
                        selectedSize === size ? 'text-emerald-700' : 'text-gray-700'
                      }`}>
                        {SIZE_LABELS[size]}
                      </span>
                      <span className={`text-xs font-light mt-0.5 ${
                        selectedSize === size ? 'text-emerald-600' : 'text-gray-400'
                      }`}>
                        {product.sizes[size].toLocaleString()} ETB
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Actions Block */}
            <div className="flex items-stretch gap-3 lg:gap-4 mb-8 transition-all duration-300">
              {product.inStock && (
                <div className="flex items-center bg-white border-2 border-emerald-100 rounded-xl lg:rounded-2xl px-2 h-12 lg:h-14 shadow-sm">
                  <button
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                    className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-emerald-50 text-gray-400 hover:text-emerald-600 disabled:opacity-20 transition-all font-bold"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-10 text-center font-bold text-lg text-gray-900">{quantity}</span>
                  <button
                    onClick={() => handleQuantityChange(1)}
                    disabled={quantity >= product.stockQuantity}
                    className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-emerald-50 text-gray-400 hover:text-emerald-600 disabled:opacity-20 transition-all font-bold"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              )}

              <button
                onClick={handleAddToCart}
                disabled={!product.inStock || (hasSizes && !selectedSize)}
                className={`flex-1 h-12 lg:h-14 rounded-xl lg:rounded-2xl font-normal text-sm sm:text-base lg:text-lg transition-all duration-500 flex items-center justify-center gap-2 shadow-lg active:scale-95 ${
                  product.inStock && (!hasSizes || selectedSize)
                    ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-emerald-200'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                <ShoppingCart className="w-5 h-5" />
                {!product.inStock
                  ? 'Out of Stock'
                  : hasSizes && !selectedSize
                  ? 'Select a size'
                  : 'Add to Cart'}
              </button>
            </div>

            <div className="space-y-8 lg:space-y-10">
              <div className="relative">
                <div className="flex items-center gap-3 lg:gap-4 mb-4 lg:mb-6">
                  <h3 className="text-[10px] lg:text-sm font-black text-gray-300 uppercase tracking-[0.2em] whitespace-nowrap">
                    Product details
                  </h3>
                  <div className="h-[1px] lg:h-[2px] flex-1 bg-gradient-to-r from-gray-100 to-transparent"></div>
                </div>

                <div className="prose prose-emerald max-w-none">
                  <p className="text-gray-600 text-base lg:text-lg leading-relaxed font-normal italic">
                    {product.description}
                  </p>
                </div>
              </div>

              {product.tags && product.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-4">
                  {product.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-gray-50 text-gray-500 text-[11px] font-bold rounded-full uppercase tracking-widest hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-default"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
